import {useEffect, useState} from 'react';
import { useParams } from 'react-router-dom';
import Form, { type OnSubmit } from 'form5/react/Form';
import Button from 'form5/react/Button';
import { toast } from 'sonner';

import type { AudienceRouteParams } from '…/app/route-params.mts';
import { useStore } from '…/app/state/useStore.mts';
import { Skeleton } from '…/app/common/Skeleton/Skeleton.js';

import { Radios } from '…/app/common/Radios/Radios.js';
import { RadioProps } from '…/app/common/Radios/Radio.js';
import { formatDate } from '…/app/common/dateUtils.mjs';
import { SubmitButton } from '…/app/common/SubmitButton/SubmitButton.jsx';

import type { HivebriteSource } from './types.js';
import { SEGMENT_LABELS, SEGMENT_TYPE } from './constants.ts';
import { HivebriteSourceInformation } from './HivebriteSourceInformation.js';
import type { IntegrationJob } from './queries/searchIntegrationJobs.op.mts';
import { syncIntegrationMembers } from './queries/syncIntegrationMembers.op.mts';
import { getIntegrationListCount } from './queries/getIntegrationListCount.op.mjs';

interface HivebriteExistingImportsFormProps {
  integrationJobs: IntegrationJob[];
  onSubmit: () => void;
}

type HivebriteMetadataType = keyof typeof SEGMENT_TYPE;

const HivebriteExistingImportsForm = ({ integrationJobs, onSubmit }: HivebriteExistingImportsFormProps) => {
  const { audienceId, workspaceId } = useParams() as AudienceRouteParams;

  const workspace = useStore((data) => data.workspaces[workspaceId]);
  const integration = workspace.integrations.find((obj) => obj.accountType === 'HIVEBRITE');
  const integrationId = integration?.id as string;

  const [isImportingMembers, setIsImportingMembers] = useState<boolean>(false);
  const [selectedImport, setSelectedImport] = useState<string | null>(null);
  const [sourceData, setSourceData] = useState<HivebriteSource | null>(null);
  const [isLoadingSourceData, setIsLoadingSourceData] = useState<boolean>(false);

  // useEffect to fetch source information when a selection is made
  useEffect(() => {
    if (selectedImport) {
      setIsLoadingSourceData(true);

      const importInfo = integrationJobs.find(({ jobRunId }) => jobRunId === selectedImport);

      if (importInfo) {
        console.log('importInfo', importInfo);

        getIntegrationListCount(integrationId, importInfo.metadata.list_ids, importInfo.metadata.list_type)
          .then(({ membersCounter }) => {
            setSourceData({
              id: importInfo.metadata.list_ids[0],
              total: membersCounter.total,
            });
          })
          .catch((error) => {
            // FIXME:
            console.error('🛑 ERRORS:', error.errors.map((e: { message: string }) => e.message).join(', '));
          })
          .finally(() => setIsLoadingSourceData(false));
      }
    }
  }, [selectedImport, integrationJobs]);

  const importRadios: Pick<RadioProps, 'description' | 'label' | 'value'>[] = integrationJobs.map(({
    createdAt, displayName, jobRunId, metadata, // status, progress,
  }) => {
    // Get type from metadata, handle the case when type doesn't exist in SEGMENT_LABELS
    const typeValue = metadata?.type;
    let segmentLabel = 'Import';

    // Check if typeValue is a valid key in SEGMENT_TYPE
    if (typeValue && Object.keys(SEGMENT_TYPE).includes(typeValue as string)) {
      const type = typeValue as HivebriteMetadataType;
      segmentLabel = SEGMENT_LABELS[type];
    }

    return {
      description: `${segmentLabel} - Last imported ${formatDate(createdAt)}`,
      label: displayName,
      value: jobRunId,
    };
  });

  const handleOnChange = (value: string) => {
    setSelectedImport(value);
  };

  const handleOnSubmit: OnSubmit = async () => {
    const importJob = integrationJobs.find(({ jobRunId }) => jobRunId === selectedImport);

    await syncIntegrationMembers(
      audienceId,
      integrationId,
      [sourceData.id as string],
      importJob.metadata.list_type,
    )
      .then(({ jobRunId }) => {
        toast.message(`Importing members from ${importJob.metadata.list_type}...`, {
          description: `Job ID: ${jobRunId}`,
        });
        onSubmit();
      })
      .catch((error) => {
        toast.error('Importing members failed', {
          description: error.message,
        });
      })
      .finally(() => {
        setIsImportingMembers(false);
        onSubmit();
      });

    onSubmit();
  };

  return (
    <Form name="hivebrite-import" onSubmit={handleOnSubmit}>
      <div className="border-b-gray-300 border-b border-solid pb-4 max-h-[300px] overflow-y-auto">
        <Radios
          name="existing-import-id"
          onChange={handleOnChange}
          options={importRadios}
          value={selectedImport}
        />
      </div>

      {isLoadingSourceData
        ? (
          <div className="mt-3">
            <Skeleton widthPercentage={25} />

            <Skeleton />
          </div>
        )
        : null}

      {
        sourceData && !isLoadingSourceData
          ? <HivebriteSourceInformation numberOfMembers={sourceData.total} />
          : null
      }

      <SubmitButton
        appearance={Button.APPEARANCES.AFFIRMING}
        className="mt-3"
        disabled={!selectedImport}
        isSubmitting={isImportingMembers}
        type="submit"
      >
        Add{`${sourceData ? ` ${sourceData.total} ` : ' '}`}members
      </SubmitButton>
    </Form>
  );
};


HivebriteExistingImportsForm.displayName = 'HivebriteExistingImportsForm';

export { HivebriteExistingImportsForm };
